import { Dayjs } from 'dayjs';
import React, { useCallback } from 'react';

import { SelectedState } from '../CalendarBody/CalendarDay';
import CalendarWeekHeader from '../CalendarWeekHeader';
import DateButtonGroup, { SelectedButton } from '../DateButtonGroup';
import Dialog, { Props as DialogProps } from '../Dialog';
import Popup from '../Popup';
import DatePickerBody from './DatePickerBody';
import SummaryRangeText from './SummaryRangeText';

import cx from '../../utils/classnames';

import styles from './datePicker.module.scss';

export type DisplayType = 'popup' | 'dialog';

export type PerDayComponentProps = { day: Dayjs; selectedState: SelectedState };

type PopupProps = {
  /** Anchor element */
  anchorEl?: HTMLElement | null;
  /** Custom min-width of the popup */
  minWidth?: number;
};

export type Props = {
  /** Custom style for wrapper */
  className: string;
  /** Custom style for the Dialog */
  dialogClassName?: string;
  /** Custom props for the Dialog component
   * className, isOpen and onClose will be overridden */
  dialogProps?: Partial<DialogProps>;
  /** End date of selected range */
  endDate?: string;
  /** String to define title of end date */
  endDateLabel: React.ReactNode;
  /** Flag to define whether the date-picker will be shown or not */
  isOpen: boolean;
  /** Number of displayed months (The first month will be started from referenceDate by default) */
  numberOfMonths: number;
  /** Callback function when the date-picker is closed */
  onClose: () => void;
  /** Callback function when the clear button is clicked */
  onClear: () => void;
  /** Callback function when the date button is clicked */
  onSelectButton: (event: React.MouseEvent, button: SelectedButton) => void;
  /** Callback function when the day item is clicked */
  onSelectDate: (event: React.MouseEvent, day: Dayjs) => void;
  /** Custom props for the custom popup component */
  popupProps?: PopupProps;
  /** Current selected button */
  selectedButton?: string;
  /** Date to define the first calendar */
  referenceDate: string;
  /** Start date of selected range */
  startDate?: string;
  /** String to define title of start date */
  startDateLabel: React.ReactNode;
  /** Decides using popup or dialog for calendar container */
  type: DisplayType;
  /** Translation labels */
  /** String to define clear button text */
  clearLabelText?: React.ReactNode;
  /** Localization values */
  /** Array of string to define title of month */
  monthLabels?: string[];
  /** Array of string to define title of day (normal-ordering) */
  weekdayLabels?: string[];
  /** week type:
   * 'week': start from Sunday
   * 'isoWeek': start from Monday
   */
  weekType?: 'week' | 'isoWeek';
  /** Date component to specify custom format */
  DateComponent: React.ComponentType<{ value: string; format?: string }>;
  /** Date to end of reference  */
  referenceEndDate?: string;
  /** Flag to define whether the dialog is displayed on top of another dialog or not */
  isDisplayedOnDialog?: boolean;
  NightsLabelComponent?: React.ComponentType<{ noOfNights: number }>;
  /** Flag to highlight the current date */
  shouldShowCurrentDate?: boolean;
  /** Custom style for wrapper of calendar day */
  dayWrapperClassName?: string;
  /** Extra component to display on calendar per day */
  PerDayComponent?: React.ComponentType<PerDayComponentProps>;
  /** Custom Height for Calendar of each month */
  customCalendarHeight?: number;
  /** Custom format for display */
  labelDateFormat?: string;
  /** Custom style for wrapper of calendar week headers */
  calendarWeekHeaderClassName?: string;
  /** Custom node for bottom info box (popup style) */
  customPopupInfoBoxComponent?: React.ReactNode;
  /** Custom style for calendar body wrapper (table) */
  calendarBodyClass?: string;
  /** Date Selection Highlight type (default circle) */
  highlightType?: 'circle' | 'full';
};

function DatePicker(props: Props) {
  const {
    className,
    dialogClassName,
    isOpen,
    popupProps,
    numberOfMonths,
    startDate,
    endDate,
    onSelectDate,
    referenceDate,
    type,
    dialogProps,
    endDateLabel,
    startDateLabel,
    onSelectButton,
    selectedButton,
    onClose,
    onClear,
    weekType,
    weekdayLabels,
    monthLabels,
    clearLabelText,
    DateComponent,
    referenceEndDate,
    isDisplayedOnDialog,
    NightsLabelComponent,
    shouldShowCurrentDate,
    dayWrapperClassName,
    PerDayComponent,
    customCalendarHeight,
    labelDateFormat,
    calendarWeekHeaderClassName,
    customPopupInfoBoxComponent,
    calendarBodyClass,
    highlightType = 'circle',
  } = props;

  // selectedButton state is handled from parent
  const onClickStartDateButton = useCallback(
    (event: React.MouseEvent) => onSelectButton(event, 'startDate'),
    [onSelectButton],
  );
  const onClickEndDateButton = useCallback(
    (event: React.MouseEvent) => onSelectButton(event, 'endDate'),
    [onSelectButton],
  );

  const handleClear = useCallback(() => {
    onClear();
  }, [onClear]);

  // isOpen state is handled from parent
  if (!isOpen) return null;

  // default props
  const defaultDatePickerProps = {
    startDate,
    endDate,
    referenceDate,
    numberOfMonths,
    onClickDayItem: onSelectDate,
    type,
    weekType,
    weekdayLabels,
    monthLabels,
    clearLabelText,
    referenceEndDate,
    shouldShowCurrentDate,
    dayWrapperClassName,
    PerDayComponent,
    customCalendarHeight,
    labelDateFormat,
    calendarWeekHeaderClassName,
    calendarBodyClass,
    highlightType,
  };

  return (
    <>
      {isOpen && type === 'popup' && (
        <Popup
          isOpen={isOpen}
          onClose={onClose}
          className={cx(className, styles.dialogContentBox)}
          style={popupProps && { minWidth: popupProps.minWidth }}
          anchorEl={popupProps && popupProps.anchorEl}
        >
          <DatePickerBody
            {...defaultDatePickerProps}
            isShowNavigation={true}
            isShowWeekHeader={true}
          />
          {customPopupInfoBoxComponent ? (
            customPopupInfoBoxComponent
          ) : (
            <div className={styles.infoBox}>
              <SummaryRangeText
                startDateLabel={startDateLabel}
                endDateLabel={endDateLabel}
                startDate={startDate}
                endDate={endDate}
                DateComponent={DateComponent}
                weekdayLabels={weekdayLabels}
                NightsLabelComponent={NightsLabelComponent}
              />
              <button
                className={styles.clearButton}
                disabled={Boolean(!startDate && !endDate)}
                onClick={handleClear}
                data-testid="datePicker-clearButton"
              >
                {clearLabelText}
              </button>
            </div>
          )}
        </Popup>
      )}

      {isOpen && type === 'dialog' && (
        <Dialog
          title={
            <div className={styles.dialogTitle}>
              {selectedButton === 'startDate' ? startDateLabel : endDateLabel}{' '}
              <button
                className={cx(styles.clearButton, styles.clearButtonInDialog)}
                disabled={Boolean(!startDate && !endDate)}
                onClick={handleClear}
                data-testid="datePicker-clearButton"
              >
                {clearLabelText}
              </button>
            </div>
          }
          {...dialogProps}
          className={cx(dialogClassName, styles.dialog)}
          isOpen={isOpen}
          onClose={onClose}
          actionClassName={styles.dialogAction}
          contentClassName={cx(className, styles.dialogContentBox, styles.typeDialog)}
          headerClassName={styles.dialogHeader}
          isUseContentFlexBox={false}
          isDisplayedOnDialog={isDisplayedOnDialog}
        >
          <DateButtonGroup
            className={styles.dateButtonGroup}
            buttonClassName={styles.dialogDateButton}
            customStyle="labelWithDate"
            isShowSelectedState={true}
            startDate={startDate}
            endDate={endDate}
            selectedButton={selectedButton}
            onClickStartDateButton={onClickStartDateButton}
            onClickEndDateButton={onClickEndDateButton}
            startDateLabel={startDateLabel}
            endDateLabel={endDateLabel}
            DateComponent={DateComponent}
          />
          <CalendarWeekHeader
            className={cx(styles.calendarWeekHeader, calendarWeekHeaderClassName)}
            itemClassName={styles.calendarWeekHeaderItem}
            weekType={weekType}
            weekdayLabels={weekdayLabels}
          />
          <DatePickerBody
            className={styles.typeDialog}
            isShowNavigation={false}
            isShowWeekHeader={false}
            {...defaultDatePickerProps}
          />
        </Dialog>
      )}
    </>
  );
}

DatePicker.defaultProps = {
  className: '',
  isOpen: false,
  type: 'popup',
  clearLabelText: 'Clear',
  onClose: () => {},
  onClear: () => {},
  onSelectButton: () => {},
  onSelectDate: () => {},
  DateComponent: ({ value }: { value: string }) => value,
};

export default DatePicker;
